import React, { useState, useEffect } from 'react';
import { SERVICES, ViewState } from '../types';
import { ArrowRight, Sparkles, Star, CheckCircle2, Clock } from 'lucide-react';

interface ServicesPageProps {
  onBook: (serviceId?: string) => void;
  filterCategories?: string[];
  view?: ViewState;
}

export function ServicesPage({ onBook, filterCategories, view }: ServicesPageProps) {
  const categories = Array.from(new Set(SERVICES.map(s => s.category)));
  const [selectedCategory, setSelectedCategory] = useState(categories[0]);

  useEffect(() => {
    if (filterCategories && filterCategories.length > 0) {
      // Find first matching category
      const firstMatch = categories.find(c => filterCategories.includes(c));
      if (firstMatch) {
        setSelectedCategory(firstMatch);
      }
    }
  }, [filterCategories, categories]);

  const filteredServices = SERVICES.filter(s => s.category === selectedCategory);

  const pageTitle = view === 'hair-salon' ? "Premium Hair Salon Abu Dhabi" :
    view === 'nail-salon' ? "Best Nail Salon Al Rawdah Abu Dhabi" :
      view === 'skin-treatments' ? "Aesthetic & Skin Treatments Abu Dhabi" :
        view === 'lash-brow' ? "Professional Lash & Brow Salon Abu Dhabi" :
          view === 'bridal' ? "Luxury Bridal Makeup & Prep Abu Dhabi" :
            "Luxury Beauty Services Abu Dhabi";

  const pageDescription = view === 'hair-salon' ? "Expert balayage, precision haircutting, and premium hair treatments in Abu Dhabi at Aloft Hotel." :
    view === 'nail-salon' ? "Award-winning manicures, long-lasting builder gel, and creative nail art in Al Rawdah, Abu Dhabi." :
      "Discover a curated menu of premium beauty and aesthetic treatments at Abu Dhabi's premier luxury lounge. From mesotherapy to bridal prep, we deliver excellence.";

  return (
    <div className="bg-stone-50 min-h-screen pt-40 pb-32">
      <div className="max-w-7xl mx-auto px-6">
        {/* Breadcrumbs (SEO Optimized) */}
        <nav aria-label="Breadcrumb" className="mb-12 fade-in-up">
          <ol className="flex items-center gap-3 text-[9px] uppercase tracking-[0.2em] font-bold">
            <li>
              <button onClick={() => window.history.back()} className="text-mier/40 hover:text-mier transition-colors">Home</button>
            </li>
            <li className="text-mier/20">/</li>
            <li>
              <span className="text-mier" aria-current="page">
                {selectedCategory === 'All' ? 'Services' : selectedCategory}
              </span>
            </li>
          </ol>
        </nav>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-24 fade-in-up">
          <div>
            <p className="text-mier-light uppercase tracking-[0.3em] text-[10px] md:text-xs font-medium mb-6">Refined Mastery</p>
            <h1 className="font-serif text-5xl md:text-7xl text-mier font-light mb-8 leading-tight">
              {pageTitle}
            </h1>
            <p className="text-stone-500 text-lg md:text-xl font-light leading-relaxed max-w-xl">
              {pageDescription}
            </p>
          </div>
          <div className="aspect-[16/9] rounded-3xl overflow-hidden shadow-2xl border border-mier/5">
            <img
              src="https://plus.unsplash.com/premium_photo-1664301489002-2fed4596c101?q=80&w=870&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
              alt={`${pageTitle} at MIER Beauty Lounge Al Rawdah Abu Dhabi`}
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Category Navigation */}
        <div className="flex flex-wrap gap-4 mb-16 border-b border-mier/10 pb-8 fade-in-up">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-6 py-3 text-[10px] uppercase tracking-[0.2em] transition-all duration-300 rounded-full ${selectedCategory === cat
                  ? 'bg-mier text-stone-50 shadow-lg'
                  : 'bg-white text-mier-light hover:bg-mier/5 border border-mier/5'
                }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Services Grid */}
        <div className="grid lg:grid-cols-[1fr_350px] gap-16 items-start">
          <div className="space-y-6 fade-in-up">
            {filteredServices.map((service) => (
              <div
                key={service.id}
                className="group bg-white border border-mier/5 hover:border-mier/20 hover:shadow-2xl hover:-translate-y-1 transition-all duration-500 p-8 md:p-10 rounded-sm flex flex-col md:flex-row md:items-center justify-between gap-8 cursor-pointer"
                onClick={() => onBook(service.id)}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <h2 className="text-2xl md:text-3xl text-mier font-medium font-serif group-hover:text-mier-dark transition-colors">{service.name}</h2>
                    {service.id.includes('balayage') && <Sparkles className="w-4 h-4 text-mier-light animate-pulse" />}
                  </div>
                  <p className="text-stone-500 text-sm md:text-base leading-relaxed max-w-xl font-light">
                    {service.description}
                  </p>
                  <div className="mt-6 flex flex-wrap gap-4">
                    <span className="text-[9px] uppercase tracking-widest text-mier-light flex items-center gap-1.5 bg-stone-50 px-3 py-1 rounded-full border border-mier/5">
                      <CheckCircle2 className="w-3 h-3" /> Premium Products
                    </span>
                    <span className="text-[9px] uppercase tracking-widest text-mier-light flex items-center gap-1.5 bg-stone-50 px-3 py-1 rounded-full border border-mier/5">
                      <CheckCircle2 className="w-3 h-3" /> Expert Stylists
                    </span>
                  </div>
                </div>
                <div className="flex flex-col items-start md:items-end gap-6 shrink-0 border-t md:border-t-0 md:border-l border-mier/5 pt-6 md:pt-0 md:pl-10">
                  <div className="flex flex-col items-start md:items-end">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-mier-light mb-1">Starts from</span>
                    <span className="font-serif text-3xl md:text-4xl text-mier tracking-tight">{service.price}</span>
                  </div>
                  <button className="bg-mier text-stone-50 px-8 py-3.5 text-[10px] uppercase tracking-[0.2em] font-medium hover:bg-mier-dark transition-colors shadow-lg group-hover:scale-105 duration-300">
                    Book Service
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Sticky Sidebar Info */}
          <aside className="lg:sticky lg:top-32 space-y-8 fade-in-up">
            <div className="bg-mier text-stone-50 p-8 rounded-sm shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-10">
                <Sparkles className="w-20 h-20" />
              </div>
              <h3 className="font-serif text-2xl mb-4 relative z-10">Experience MIER</h3>
              <p className="text-stone-300 text-sm leading-relaxed mb-8 font-light relative z-10">
                Each treatment at MIER Beauty Lounge is a bespoke journey tailored to your unique needs. We use only the world's finest products to ensure exceptional results.
              </p>
              <div className="space-y-4 mb-8 relative z-10">
                <div className="flex items-center gap-3 text-xs tracking-wide">
                  <Star className="w-4 h-4 text-yellow-500 fill-current" />
                  <span>5.0 Rating for {selectedCategory}</span>
                </div>
                <div className="flex items-center gap-3 text-xs tracking-wide">
                  <CheckCircle2 className="w-4 h-4 text-stone-400" />
                  <span>Sterilized Equipment</span>
                </div>
              </div>
              <button
                onClick={() => onBook()}
                className="w-full bg-stone-50 text-mier py-4 text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-mier-dark hover:text-stone-50 transition-all duration-300 shadow-xl"
              >
                Inquire via WhatsApp
              </button>
            </div>

            <div className="bg-white p-8 border border-mier/5 rounded-sm">
              <h3 className="font-serif text-xl mb-4 text-mier">Policy</h3>
              <ul className="text-xs space-y-4 text-stone-500 font-light leading-relaxed">
                <li className="flex gap-3">
                  <span className="w-1.5 h-1.5 bg-mier-light rounded-full mt-1.5 shrink-0"></span>
                  Please arrive 10 minutes prior to your appointment.
                </li>
                <li className="flex gap-3">
                  <span className="w-1.5 h-1.5 bg-mier-light rounded-full mt-1.5 shrink-0"></span>
                  Cancellations require 24 hours notice.
                </li>
                <li className="flex gap-3">
                  <span className="w-1.5 h-1.5 bg-mier-light rounded-full mt-1.5 shrink-0"></span>
                  Complimentary valet parking at Aloft Hotel.
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
